import type { SpotListItem } from "@divespots/contracts";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSession } from "../admin/session.ts";
import { useFavourites, useToggleFavourite } from "../api/index.ts";
import { loginUrl } from "../auth/next.ts";
import { SheetButton } from "../components/Sheet.tsx";
import { useLocale } from "../i18n/index.ts";

interface Props {
  spot: Pick<SpotListItem, "id" | "slug" | "status">;
}

/**
 * The star on a spot (sheet header). A filled star means favourite, an outlined one does not (never
 * colour alone). Only published spots have one (the API refuses the rest); signed out, it leads to
 * the login page and back here.
 */
export function FavouriteButton({ spot }: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const { status } = useSession();
  const favourites = useFavourites();
  const toggle = useToggleFavourite();
  if (spot.status !== "published") return null;

  const on = favourites.ids.has(spot.id);
  const label = on ? t("favourites.remove") : t("favourites.add");
  const signedIn = status === "user";
  return (
    <SheetButton
      aria-label={label}
      aria-pressed={on}
      className="sheet-btn sheet-btn--favourite"
      disabled={status === "loading" || (signedIn && favourites.isPending)}
      icon={
        <Star aria-hidden="true" size={16} strokeWidth={2} fill={on ? "currentColor" : "none"} />
      }
      onClick={() => {
        if (!signedIn) void navigate(loginUrl(locale, `/${locale}/spot/${spot.slug}`));
        else void toggle(spot.id, !on);
      }}
    />
  );
}
